// loading.js
// Enhanced loading system with task tracking and resource waiting
// Usage: 
//   - Simple: import { showLoading, hideLoading } from './loading.js';
//   - Advanced: import { createLoadingManager } from './loading.js';

import { IMAGE_PATHS, APP_VERSION } from '/settings.js';

/**
 * Adds version query parameter to URL for cache-busting
 */
function addVersion(url) {
    if (!APP_VERSION) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${APP_VERSION}`;
}

import { isAssetPreloaded } from '../utils/cache/index.js';

const ANIMATION_CLASSES = ['bounce-effect', 'grow-effect'];

// Configuration
const CONFIG = {
    MIN_DISPLAY_TIME: 1000, // Minimum time to show loading (ms) to avoid flicker
    MAX_WAIT_TIME: 8000,   // Maximum wait time before forcing hide (ms)
    FADE_OUT_DURATION: 500 // CSS transition duration (ms)
};

// Global state
let loadingManager = null;
let loadingStartTime = null;
let criticalTasks = new Set();
let completedTasks = new Set();

/**
 * Simple API: Show loading overlay
 * Logo is now inlined in HTML for immediate display
 * This function just ensures it's visible and adds animation
 */
export function showLoading() {
    let loading = document.getElementById('app-loading');
    let logo;

    if (!loading) {
        // Fallback: create loading overlay if it doesn't exist (shouldn't happen with inlined HTML)
        loading = document.createElement('div');
        loading.id = 'app-loading';
        loading.className = 'app-loading-overlay';

        const logoUrl = addVersion(IMAGE_PATHS.LOGO);
        const logoImg = document.createElement('img');
        logoImg.src = logoUrl;
        logoImg.alt = 'Logo';
        logoImg.className = 'app-loading-logo';
        logoImg.id = 'app-loading-logo';

        loading.innerHTML = `
            <div class="app-loading-content">
            </div>
        `;
        const content = loading.querySelector('.app-loading-content');
        content.appendChild(logoImg);
        document.body.appendChild(loading);
        logo = logoImg;
    } else {
        // Logo is already in HTML (inlined), just get reference
        logo = document.getElementById('app-loading-logo') || loading.querySelector('.app-loading-logo');

        // If logo doesn't exist, create it (fallback)
        if (!logo) {
            const content = loading.querySelector('.app-loading-content');
            if (content) {
                const logoImg = document.createElement('img');
                logoImg.src = addVersion(IMAGE_PATHS.LOGO);
                logoImg.alt = 'Logo';
                logoImg.className = 'app-loading-logo';
                logoImg.id = 'app-loading-logo';
                content.appendChild(logoImg);
                logo = logoImg;
            }
        }
    }

    // Remove any previous animation class
    if (logo) {
        ANIMATION_CLASSES.forEach(cls => logo.classList.remove(cls));
        // Pick a random animation
        const randomClass = ANIMATION_CLASSES[Math.floor(Math.random() * ANIMATION_CLASSES.length)];
        logo.classList.add(randomClass);
    }

    // Ensure loading overlay is visible
    loading.style.display = '';
    setTimeout(() => {
        loading.classList.add('visible');
    }, 10);

    // Track start time
    loadingStartTime = Date.now();
}

/**
 * Simple API: Hide loading overlay (immediate)
 */
export function hideLoading() {
    const loading = document.getElementById('app-loading');
    if (loading) {
        loading.classList.remove('visible');
        setTimeout(() => {
            loading.style.display = 'none';
        }, CONFIG.FADE_OUT_DURATION);
    }
    // Reset state
    loadingStartTime = null;
    criticalTasks.clear();
    completedTasks.clear();
}

/**
 * Enhanced API: Create a loading manager that tracks tasks
 * @returns {Object} Loading manager with methods to track tasks
 */
export function createLoadingManager() {
    if (loadingManager) {
        return loadingManager;
    }

    loadingManager = {
        /**
         * Register a critical task that must complete before hiding loading
         * @param {string} taskId - Unique identifier for the task
         * @returns {Function} Complete function to call when task is done
         */
        registerTask(taskId) {
            criticalTasks.add(taskId);
            return () => {
                this.completeTask(taskId);
            };
        },

        /**
         * Mark a task as complete
         * @param {string} taskId - Task identifier
         */
        completeTask(taskId) {
            if (criticalTasks.has(taskId)) {
                completedTasks.add(taskId);
                this.checkIfReady();
            }
        },

        /**
         * Wait for a promise and mark it as a task
         * @param {string} taskId - Task identifier
         * @param {Promise} promise - Promise to wait for
         */
        async waitForTask(taskId, promise) {
            const complete = this.registerTask(taskId);
            try {
                await promise;
                complete();
            } catch (error) {
                console.error(`[Loading] Task ${taskId} failed:`, error);
                complete(); // Still mark as complete to avoid blocking
            }
        },

        /**
         * Wait for map to finish loading tiles
         * @param {L.Map} map - Leaflet map instance
         */
        waitForMap(map) {
            if (!map) return;

            const taskId = 'map-load';
            const complete = this.registerTask(taskId);

            // Leaflet map fires 'load' event when initial tiles are loaded
            // Also listen for 'tilelayerload' which fires when tile layers finish loading
            let loadComplete = false;

            const onMapLoad = () => {
                if (loadComplete) return;
                loadComplete = true;
                map.off('load', onMapLoad);
                map.off('tilelayerload', onMapLoad);
                // Small delay to ensure tiles are rendered
                setTimeout(complete, 100);
            };

            // Listen for both map load and tile layer load events
            map.on('load', onMapLoad);
            map.on('tilelayerload', onMapLoad);

            // Fallback: if map doesn't fire load event, complete after reasonable time
            // Since we use GeoJSON mostly (no tiles), this will fire quickly
            setTimeout(() => {
                if (!loadComplete) {
                    map.off('load', onMapLoad);
                    map.off('tilelayerload', onMapLoad);
                    complete();
                }
            }, 300); // Reduced from 3000ms to 300ms as GeoJSON is instant
        },

        /**
         * Wait for resources (images, fonts) to load
         */
        async waitForResources() {
            const taskId = 'resources';
            const complete = this.registerTask(taskId);

            try {
                // Wait for logo image - use existing element if possible
                const existingLogo = document.getElementById('app-loading-logo');
                if (existingLogo) {
                    if (!existingLogo.complete) {
                        await new Promise((resolve) => {
                            existingLogo.onload = resolve;
                            existingLogo.onerror = resolve;
                            setTimeout(resolve, 1000); // Timeout after 1s
                        });
                    }
                } else {
                    // Fallback for when logo isn't in DOM yet
                    const logo = new Image();
                    await new Promise((resolve, reject) => {
                        logo.onload = resolve;
                        logo.onerror = resolve;
                        logo.src = addVersion(IMAGE_PATHS.LOGO);
                        setTimeout(resolve, 1000); // Timeout after 1s
                    });
                }

                // Wait for fonts if available
                if (document.fonts && document.fonts.ready) {
                    await Promise.race([
                        document.fonts.ready,
                        new Promise(resolve => setTimeout(resolve, 1000))
                    ]);
                }

                complete();
            } catch (error) {
                console.error('[Loading] Resource loading error:', error);
                complete(); // Don't block on errors
            }
        },

        /**
         * Check if all tasks are complete and hide loading if ready
         */
        checkIfReady() {
            const allComplete = criticalTasks.size > 0 &&
                criticalTasks.size === completedTasks.size;

            if (!allComplete) return;

            const elapsed = Date.now() - (loadingStartTime || 0);
            const remainingTime = Math.max(0, CONFIG.MIN_DISPLAY_TIME - elapsed);

            // Wait for minimum display time, then hide
            setTimeout(() => {
                hideLoading();
                loadingManager = null;
            }, remainingTime);
        },

        /**
         * Force hide loading (override all checks)
         */
        forceHide() {
            hideLoading();
            loadingManager = null;
        },

        /**
         * Reset the loading manager
         */
        reset() {
            criticalTasks.clear();
            completedTasks.clear();
            loadingStartTime = Date.now();
        }
    };

    // Set fallback timeout
    setTimeout(() => {
        if (loadingManager && criticalTasks.size > 0) {
            console.warn('[Loading] Maximum wait time reached, forcing hide');
            loadingManager.forceHide();
        }
    }, CONFIG.MAX_WAIT_TIME);

    return loadingManager;
}

/**
 * Enhanced API: Initialize loading with automatic task tracking
 * Call this at the start of app initialization
 * @returns {Object} Loading manager
 */
export function initializeLoading() {
    showLoading();
    return createLoadingManager();
}
